/**
 * pvp_context.ts — the client's read of the ground PvP sessions (2026-09-18):
 * my live duel, my live match and its members, my enrollment and its
 * sector, and the LEGAL player targets those imply. A mirror of the
 * server's pvp_policy for the purpose of never OFFERING an illegal mark —
 * the server still decides (a refusal costs nothing and names its reason);
 * sanctuary and recovery protection are its alone.
 */
import { atlas } from '../state.js';

interface IdLike { toHexString(): string; isEqual(o: unknown): boolean }
interface TsLike { microsSinceUnixEpoch: bigint }
export interface DuelRow {
  duelId: bigint; a: IdLike; b: IdLike; state: string; ringX: number; ringZ: number; ringR: number; readyA: boolean; readyB: boolean;
  outSinceA: TsLike; outSinceB: TsLike; gen: number; invitedAt: TsLike; startsAt: TsLike; endsAt: TsLike; winner: IdLike; result: string; finishedAt: TsLike;
}
export interface MatchRow {
  matchId: bigint; state: string; teamSize: number; round: number; winsA: number; winsB: number; draws: number;
  relayX: number; relayZ: number; relayR: number; stagingAx: number; stagingAz: number; stagingBx: number; stagingBz: number;
  capA: number; capB: number; roundEndsAt: TsLike; relayActiveAt: TsLike; roundStartedAt: TsLike; readyDeadline: TsLike; winner: string; result: string; finishedAt: TsLike;
}
export interface MemberRow { memberId: bigint; matchId: bigint; identity: IdLike; team: string; ready: boolean; alive: boolean; absent: boolean; active: boolean }
export interface EnrollRow { identity: IdLike; sectorId: bigint; side: string; state: string; stateAt: TsLike; lockUntil: TsLike; leaveAt: TsLike }
export interface SectorRow { sectorId: bigint; name: string; x: number; z: number; r: number; relayR: number; sideA: string; sideB: string; active: boolean; capA: number; capB: number; scoreA: number; scoreB: number }
export interface ActorRow { identity: IdLike; life: number; attackReadyAt: TsLike; equipReadyAt: TsLike; pendingActionId: bigint; combatLockUntil: TsLike; protectedUntil: TsLike }
export interface ResultRow { resultId: bigint; actionId: bigint; attacker: IdLike; targetKind: string; targetPlayer: IdLike; targetId: bigint; outcome: string; dmg: number; itemKey: string; contextKind: string; at: TsLike }
export interface ActionRow { scheduledId: bigint; attacker: IdLike; targetKind: string; targetPlayer: IdLike; itemKey: string; startedAt: TsLike; impactAt: TsLike; contextKind: string }
export interface QueueRow { identity: IdLike; teamSize: number; siteId: bigint; joinedAt: TsLike }
export interface RecordRow { identity: IdLike; duelsWon: number; duelsLost: number; duelsDrawn: number; matchesWon: number; matchesLost: number; matchesDrawn: number; objectives: number }

type Db = Record<string, { iter(): Iterable<unknown> } | undefined>;
export function rows<T>(name: string): T[] {
  try {
    const t = (atlas.conn?.db as unknown as Db)[name];
    return t ? ([...t.iter()] as T[]) : [];
  } catch { return []; }
}
const meHex = (): string | null => atlas.identityHex;
const isMe = (id: IdLike): boolean => { const m = meHex(); return m !== null && id.toHexString() === m; };
export const usOf = (t: TsLike): number => Number(t.microsSinceUnixEpoch / 1000n);
export const nowMs = (): number => Date.now();

export function myDuel(): DuelRow | null {
  let latestFinished: DuelRow | null = null;
  for (const d of rows<DuelRow>('pvpDuel')) {
    if (!isMe(d.a) && !isMe(d.b)) continue;
    if (d.state !== 'finished') return d;
    if (!latestFinished || d.duelId > latestFinished.duelId) latestFinished = d;
  }
  return latestFinished;
}
export function pendingInvite(): DuelRow | null {
  for (const d of rows<DuelRow>('pvpDuel')) if (d.state === 'invited' && isMe(d.b)) return d;
  return null;
}
export function myMember(): MemberRow | null {
  for (const m of rows<MemberRow>('pvpArenaMember')) if (m.active && isMe(m.identity)) return m;
  return null;
}
export function matchOf(matchId: bigint): MatchRow | null {
  for (const m of rows<MatchRow>('pvpArenaMatch')) if (m.matchId === matchId) return m;
  return null;
}
export function membersOf(matchId: bigint): MemberRow[] {
  return rows<MemberRow>('pvpArenaMember').filter((m) => m.matchId === matchId);
}
export function myQueue(): QueueRow | null {
  for (const q of rows<QueueRow>('pvpArenaQueue')) if (isMe(q.identity)) return q;
  return null;
}
export function myEnrollment(): EnrollRow | null {
  for (const e of rows<EnrollRow>('pvpEnrollment')) if (isMe(e.identity)) return e;
  return null;
}
export function sectorOf(sectorId: bigint): SectorRow | null {
  for (const s of rows<SectorRow>('pvpSector')) if (s.sectorId === sectorId) return s;
  return null;
}
export function nearestSector(x: number, z: number): SectorRow | null {
  let best: SectorRow | null = null, bd = Infinity;
  for (const s of rows<SectorRow>('pvpSector')) {
    if (!s.active) continue;
    const d = Math.hypot(s.x - x, s.z - z);
    if (d < bd) { bd = d; best = s; }
  }
  return best;
}
export function myActor(): ActorRow | null {
  for (const a of rows<ActorRow>('combatActor')) if (isMe(a.identity)) return a;
  return null;
}
export function myRecord(): RecordRow | null {
  for (const r of rows<RecordRow>('pvpRecord')) if (isMe(r.identity)) return r;
  return null;
}
/** The committed blow aimed at me with the nearest impact, if any. */
export function tellAtMe(): ActionRow | null {
  let best: ActionRow | null = null;
  for (const a of rows<ActionRow>('combatAction')) {
    if (a.targetKind !== 'player' || !isMe(a.targetPlayer)) continue;
    if (!best || usOf(a.impactAt) < usOf(best.impactAt)) best = a;
  }
  return best;
}

/** The context I am fighting in, for the HUD and the hit-stop dose. */
export type PvpContextKind = 'duel' | 'arena' | 'field' | null;
export function myContext(): PvpContextKind {
  const d = myDuel();
  if (d && d.state === 'active') return 'duel';
  const mem = myMember();
  if (mem) { const m = matchOf(mem.matchId); if (m && m.state === 'round') return 'arena'; }
  const e = myEnrollment();
  if (e && e.state !== 'enrolling') return 'field';
  return null;
}

/** The player hexes I may lawfully strike RIGHT NOW (the policy's mirror):
 *  my duel's other member while it is active; the opposing LIVE members of
 *  my match while its round runs; the opposite side's eligible/engaged in my
 *  sector. Anything else is never offered as a mark. */
export function legalPlayerTargets(): Set<string> {
  const out = new Set<string>();
  const me = meHex();
  if (!me) return out;
  const d = myDuel();
  if (d && d.state !== 'finished') {
    if (d.state === 'active') out.add(isMe(d.a) ? d.b.toHexString() : d.a.toHexString());
    return out; // inside a duel nothing else is legal
  }
  const mem = myMember();
  if (mem) {
    const m = matchOf(mem.matchId);
    if (m && m.state === 'round' && mem.alive) {
      for (const o of membersOf(mem.matchId)) if (o.team !== mem.team && o.alive) out.add(o.identity.toHexString());
    }
    return out;
  }
  const e = myEnrollment();
  if (e && (e.state === 'eligible' || e.state === 'engaged' || e.state === 'leaving')) {
    const s = sectorOf(e.sectorId);
    if (s && s.active) {
      for (const o of rows<EnrollRow>('pvpEnrollment')) {
        if (o.sectorId !== e.sectorId || o.side === e.side || o.state === 'enrolling' || isMe(o.identity)) continue;
        out.add(o.identity.toHexString());
      }
    }
  }
  return out;
}

/** A player's display name from the account table (falls back to a short hex). */
export function playerName(hex: string): string {
  for (const a of rows<{ identity: IdLike; username: string; displayName: string }>('account')) {
    if (a.identity.toHexString() === hex) return a.displayName || a.username || hex.slice(0, 8);
  }
  return hex.slice(0, 8);
}
export function identityByName(name: string): string | null {
  const n = name.toLowerCase();
  for (const a of rows<{ identity: IdLike; username: string; displayName: string }>('account')) {
    if (a.username.toLowerCase() === n || a.displayName.toLowerCase() === n) return a.identity.toHexString();
  }
  return null;
}
